import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Mail, MapPin, Send, Loader2, MessageSquare, Facebook } from "lucide-react";
import { BUSINESS, SERVICES } from "@/data/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SERVICE_OPTIONS = [...SERVICES.map((s) => s.title), "Other / Not Sure"];

const inputClass =
  "w-full bg-zinc-950 border border-zinc-800 px-4 py-3 text-white placeholder:text-zinc-600 focus:outline-none focus:border-red-600 focus:ring-1 focus:ring-red-600 transition-colors duration-150";

export const Contact = () => {
  const [form, setForm] = useState({ name: "", phone: "", email: "", service: "", message: "" });
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.service) {
      toast.error("Please fill in your name, phone and the service you need.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/leads`, form);
      toast.success("Request sent! Precision will reach out to you shortly.");
      setForm({ name: "", phone: "", email: "", service: "", message: "" });
    } catch (err) {
      console.error(err);
      toast.error("Something went wrong. Please call us at " + BUSINESS.phone);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="relative bg-zinc-950 py-24 md:py-32 border-t border-zinc-900">
      <div className="mx-auto max-w-7xl px-5 md:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16">
          {/* Left: pitch + contact info */}
          <div>
            <div className="flex items-center gap-3">
              <span className="h-px w-10 bg-red-600" />
              <span className="text-xs font-bold tracking-[0.3em] uppercase text-red-500">
                Get In Touch
              </span>
            </div>
            <h2 className="mt-4 font-heading uppercase tracking-tight text-5xl md:text-6xl text-white leading-[0.95]">
              Request A<br /><span className="text-red-600">Free Estimate</span>
            </h2>
            <p className="mt-6 text-base md:text-lg text-zinc-400 leading-relaxed max-w-md">
              Tell us about your project and we'll get right back to you. Prefer to
              talk? Call or text anytime.
            </p>

            <div className="mt-10 space-y-4">
              <a
                href={BUSINESS.phoneHref}
                data-testid="contact-phone"
                className="group flex items-center gap-4 border border-zinc-800 bg-black p-5 hover:border-red-600 transition-colors duration-150"
              >
                <span className="flex h-12 w-12 items-center justify-center bg-red-600 shrink-0">
                  <Phone className="h-5 w-5 text-white" />
                </span>
                <span>
                  <span className="block text-[10px] font-bold tracking-[0.25em] uppercase text-zinc-500">Call or Text</span>
                  <span className="block text-lg font-bold text-white group-hover:text-red-500 transition-colors duration-150">{BUSINESS.phone}</span>
                </span>
              </a>
              <a
                href={BUSINESS.emailHref}
                data-testid="contact-email"
                className="group flex items-center gap-4 border border-zinc-800 bg-black p-5 hover:border-red-600 transition-colors duration-150"
              >
                <span className="flex h-12 w-12 items-center justify-center bg-red-600 shrink-0">
                  <Mail className="h-5 w-5 text-white" />
                </span>
                <span className="min-w-0">
                  <span className="block text-[10px] font-bold tracking-[0.25em] uppercase text-zinc-500">Email</span>
                  <span className="block text-lg font-bold text-white group-hover:text-red-500 transition-colors duration-150 truncate">{BUSINESS.email}</span>
                </span>
              </a>
              <div className="flex items-center gap-4 border border-zinc-800 bg-black p-5">
                <span className="flex h-12 w-12 items-center justify-center bg-zinc-800 shrink-0">
                  <MapPin className="h-5 w-5 text-red-500" />
                </span>
                <span>
                  <span className="block text-[10px] font-bold tracking-[0.25em] uppercase text-zinc-500">Service Area</span>
                  <span className="block text-lg font-bold text-white">{BUSINESS.serviceArea}</span>
                </span>
              </div>
              <a
                href={BUSINESS.facebook}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-facebook"
                className="group flex items-center gap-4 border border-zinc-800 bg-black p-5 hover:border-red-600 transition-colors duration-150"
              >
                <span className="flex h-12 w-12 items-center justify-center bg-red-600 shrink-0">
                  <Facebook className="h-5 w-5 text-white" />
                </span>
                <span>
                  <span className="block text-[10px] font-bold tracking-[0.25em] uppercase text-zinc-500">Follow Us</span>
                  <span className="block text-lg font-bold text-white group-hover:text-red-500 transition-colors duration-150">Facebook</span>
                </span>
              </a>
            </div>
          </div>

          {/* Right: form */}
          <div className="border border-zinc-800 bg-black p-6 md:p-9">
            <form onSubmit={submit} data-testid="lead-form" className="space-y-5">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label className="block text-xs font-bold uppercase tracking-widest text-zinc-400 mb-2">Name *</label>
                  <input data-testid="lead-name" className={inputClass} value={form.name} onChange={update("name")} placeholder="John Smith" />
                </div>
                <div>
                  <label className="block text-xs font-bold uppercase tracking-widest text-zinc-400 mb-2">Phone *</label>
                  <input data-testid="lead-phone" className={inputClass} value={form.phone} onChange={update("phone")} placeholder="865-000-0000" />
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold uppercase tracking-widest text-zinc-400 mb-2">Email</label>
                <input data-testid="lead-email" type="email" className={inputClass} value={form.email} onChange={update("email")} placeholder="you@email.com" />
              </div>

              <div>
                <label className="block text-xs font-bold uppercase tracking-widest text-zinc-400 mb-2">Service Needed *</label>
                <select data-testid="lead-service" className={inputClass} value={form.service} onChange={update("service")}>
                  <option value="" disabled>Select a service…</option>
                  {SERVICE_OPTIONS.map((s) => (
                    <option key={s} value={s} className="bg-zinc-950">{s}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs font-bold uppercase tracking-widest text-zinc-400 mb-2">Project Details</label>
                <textarea data-testid="lead-message" rows={4} className={`${inputClass} resize-none`} value={form.message} onChange={update("message")} placeholder="Tell us what you need done…" />
              </div>

              <button
                type="submit"
                data-testid="lead-submit"
                disabled={loading}
                className="group w-full inline-flex items-center justify-center gap-2 bg-red-600 hover:bg-red-700 disabled:opacity-60 px-6 py-4 text-sm font-bold uppercase tracking-widest text-white transition-colors duration-150"
              >
                {loading ? (
                  <><Loader2 className="h-4 w-4 animate-spin" /> Sending…</>
                ) : (
                  <><Send className="h-4 w-4 group-hover:translate-x-1 transition-transform duration-150" /> Send My Request</>
                )}
              </button>

              <p className="flex items-center justify-center gap-2 text-xs text-zinc-500">
                <MessageSquare className="h-3.5 w-3.5" />
                We'll reply by phone or email. No spam, ever.
              </p>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};
